import React from 'react';
import { Flag, Chip, Icon } from './primitives.jsx';

const Hearts = ({ remaining, total }) => (
  <span style={{ fontSize: 20, letterSpacing: 2 }} aria-label={`${remaining} of ${total} lives`}>
    {Array.from({ length: total }, (_, i) => (
      <span key={i} style={{ color: i < remaining ? 'var(--danger)' : 'var(--ink-faint)' }}>{i < remaining ? '♥' : '♡'}</span>
    ))}
  </span>
);

export const TipsterEliminator = ({ pool, identity }) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(null);
  const teamOf = (id) => pool.teams.find((t) => t.id === id);

  const refresh = React.useCallback(() => {
    fetch(`/api/pool/${pool.id}/eliminator?name=${encodeURIComponent(identity.handle)}`)
      .then((r) => r.json()).then(setData).catch(() => {});
  }, [pool.id, identity.handle]);
  React.useEffect(() => { refresh(); }, [refresh]);

  const pick = async (round, teamId) => {
    setError(''); setSaving(teamId);
    try {
      const res = await fetch(`/api/pool/${pool.id}/eliminator/pick`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: identity.handle, round, teamId }),
      });
      const body = await res.json();
      if (!res.ok) { setError(body.error || 'Could not save that pick.'); return; }
      refresh();
    } catch { setError('Network error.'); } finally { setSaving(null); }
  };

  const me = data?.me;
  const current = data?.currentRound;
  const inKo = data?.currentStage === 'ko';
  const livesStart = data?.livesStart ?? 2;
  const currentRoundDef = data?.rounds?.find((r) => r.key === current);

  return (
    <div>
      {error && <div style={{ color: 'var(--danger)', fontSize: 14, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 20 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 40, lineHeight: 1 }}>
          {data?.aliveCount ?? '—'}<span style={{ fontSize: 16, color: 'var(--ink-soft)' }}> / {data?.totalCount ?? '—'} still standing</span>
        </div>
        {me && <Chip tone={me.status === 'out' ? 'danger' : 'success'}>{me.status === 'out' ? 'ELIMINATED' : inKo ? 'STILL IN' : 'STILL ALIVE'}</Chip>}
        {me && me.status !== 'out' && !inKo && <Hearts remaining={me.lives} total={livesStart} />}
      </div>

      {me && me.status !== 'out' && current && (
        <section style={{ marginBottom: 24 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, textTransform: 'uppercase', margin: '0 0 10px' }}>
            {currentRoundDef ? `Your ${currentRoundDef.label} pick` : 'Make your pick'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px,1fr))', gap: 10 }}>
            {me.pickable.map(({ teamId }) => {
              const t = teamOf(teamId); const selected = me.currentPick === teamId;
              return (
                <button key={teamId} onClick={() => pick(current, teamId)} disabled={saving === teamId}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                    background: selected ? 'var(--blue)' : 'var(--paper)', color: selected ? 'var(--paper)' : 'var(--ink)',
                    border: `2px solid ${selected ? 'var(--blue)' : 'var(--hairline-strong)'}`, opacity: saving === teamId ? 0.5 : 1 }}>
                  <Flag teamId={teamId} size={28} />
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, textTransform: 'uppercase' }}>{t?.country || teamId}</span>
                  {selected && <Icon name="check" size={16} />}
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-soft)', margin: '0 0 10px' }}>Recently knocked out</h3>
        {(data?.casualties || []).length === 0 && <p style={{ color: 'var(--ink-mute)' }}>Nobody out yet.</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(data?.casualties || []).map((c, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', borderRadius: 8, background: 'var(--paper)', border: '1px solid var(--hairline)', opacity: 0.7 }}>
              <span style={{ fontFamily: 'var(--font-display)', textTransform: 'uppercase' }}>{c.displayName}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--danger)' }}>OUT · {String(c.outRound).toUpperCase()}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
